import {useContext, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Box, Stack, Typography, Card, CardContent, Button, IconButton,
    TextField, Switch, Select, MenuItem, Chip, Divider, CircularProgress,
} from "@mui/material";
import {
    IconArrowLeft, IconKey, IconServer, IconShield, IconDatabase,
    IconDeviceFloppy, IconRefresh,
} from "@tabler/icons-react";
import {toast} from "react-toastify";
import {Context} from "../../../context/AppContext";
import {KnowledgeDatabaseRepository} from "../../../repositories/KnowledgeDatabaseRepository";
import {KnowledgeManagerLayout} from "./components/KnowledgeManagerLayout";

const cardSx = {borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none"};

const SectionCard = ({icon, title, children}) => (
    <Card sx={cardSx}>
        <Box sx={{px: 3, pt: 2.5, pb: 1}}>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
                {icon}
                <Typography fontWeight={600} fontSize={15}>{title}</Typography>
            </Stack>
        </Box>
        <CardContent sx={{pt: 1}}>
            <Stack gap={2.5}>
                {children}
            </Stack>
        </CardContent>
    </Card>
);

const FieldLabel = ({children, hint}) => (
    <Stack gap={0.3}>
        <Typography fontSize={12}>{children}</Typography>
        {hint && <Typography fontSize={11} color={"text.secondary"}>{hint}</Typography>}
    </Stack>
);

export const KnowledgeManagerGeneralSettingsPage = () => {
    const {projectId} = useParams();
    const navigate = useNavigate();
    const {state} = useContext(Context);
    const {kbTopics} = state;

    const [openaiKey, setOpenaiKey] = useState("sk-xxxxxxxxxxxxxxxx");
    const [ollamaHost, setOllamaHost] = useState("http://localhost:11434");
    const [defaultPort, setDefaultPort] = useState("9621");
    const [autoHealthCheck, setAutoHealthCheck] = useState(true);
    const [healthInterval, setHealthInterval] = useState("60");
    const [loggingLevel, setLoggingLevel] = useState("info");
    const [enableAuth, setEnableAuth] = useState(true);
    const [isCheckingAll, setIsCheckingAll] = useState(false);

    const handleCheckAll = async () => {
        setIsCheckingAll(true);
        try {
            const topicsWithServer = kbTopics.filter(t => t.server);
            for (const topic of topicsWithServer) {
                await KnowledgeDatabaseRepository.checkHealth(topic.id);
            }
            toast.success("All health checks complete");
        } catch (err) {
            toast.error("Health check failed");
        } finally {
            setIsCheckingAll(false);
        }
    };

    const handleSave = () => {
        toast.success("Global settings saved");
    };

    return (
        <KnowledgeManagerLayout>
            <Box sx={{flex: 1, overflow: "auto"}}>
                <Box sx={{p: 3, maxWidth: 700, mx: "auto"}}>

                    <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{mb: 3}}>
                        <IconButton onClick={() => navigate(`/project/${projectId}/knowledge-manager`)}>
                            <IconArrowLeft size={18}/>
                        </IconButton>
                        <Box>
                            <Typography variant={"h5"} fontWeight={700}>Global Settings</Typography>
                            <Typography fontSize={13} color={"text.secondary"}>
                                Configure global settings for all LightRAG instances
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack gap={2.5}>


                        <SectionCard icon={<IconKey size={16}/>} title={"API Keys"}>
                            <Box>
                                <FieldLabel hint={"Used for GPT models and OpenAI embeddings"}>
                                    OpenAI API Key
                                </FieldLabel>
                                <TextField
                                    fullWidth size={"small"} type={"password"}
                                    value={openaiKey}
                                    onChange={(e) => setOpenaiKey(e.target.value)}
                                    sx={{mt: 0.75}}
                                />
                            </Box>
                            <Box>
                                <FieldLabel hint={"For local LLM inference via Ollama"}>
                                    Ollama Host URL
                                </FieldLabel>
                                <TextField
                                    fullWidth size={"small"}
                                    value={ollamaHost}
                                    onChange={(e) => setOllamaHost(e.target.value)}
                                    sx={{mt: 0.75}}
                                />
                            </Box>
                        </SectionCard>


                        <SectionCard icon={<IconServer size={16}/>} title={"Server Defaults"}>
                            <Stack direction={"row"} gap={2}>
                                <Box sx={{flex: 1}}>
                                    <FieldLabel>Default Starting Port</FieldLabel>
                                    <TextField
                                        fullWidth size={"small"} type={"number"}
                                        value={defaultPort}
                                        onChange={(e) => setDefaultPort(e.target.value)}
                                        sx={{mt: 0.75}}
                                    />
                                </Box>
                                <Box sx={{flex: 1}}>
                                    <FieldLabel>Health Check Interval (sec)</FieldLabel>
                                    <TextField
                                        fullWidth size={"small"} type={"number"}
                                        value={healthInterval}
                                        onChange={(e) => setHealthInterval(e.target.value)}
                                        sx={{mt: 0.75}}
                                    />
                                </Box>
                            </Stack>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <FieldLabel hint={"Periodically check server connectivity"}>
                                    Auto Health Check
                                </FieldLabel>
                                <Switch
                                    checked={autoHealthCheck}
                                    onChange={(e) => setAutoHealthCheck(e.target.checked)}
                                />
                            </Stack>
                        </SectionCard>

                        <SectionCard icon={<IconShield size={16}/>} title={"Security"}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <FieldLabel hint={"Require API key for all LightRAG requests"}>
                                    Enable Authentication
                                </FieldLabel>
                                <Switch
                                    checked={enableAuth}
                                    onChange={(e) => setEnableAuth(e.target.checked)}
                                />
                            </Stack>
                            <Box>
                                <FieldLabel>Logging Level</FieldLabel>
                                <Select
                                    fullWidth size={"small"}
                                    value={loggingLevel}
                                    onChange={(e) => setLoggingLevel(e.target.value)}
                                    sx={{mt: 0.75}}
                                >
                                    <MenuItem value={"debug"}>Debug</MenuItem>
                                    <MenuItem value={"info"}>Info</MenuItem>
                                    <MenuItem value={"warning"}>Warning</MenuItem>
                                    <MenuItem value={"error"}>Error</MenuItem>
                                </Select>
                            </Box>
                        </SectionCard>


                        <Card sx={cardSx}>
                            <Box sx={{px: 3, pt: 2.5, pb: 1}}>
                                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                                        <IconDatabase size={16}/>
                                        <Typography fontWeight={600} fontSize={15}>Server Overview</Typography>
                                    </Stack>
                                    <Button
                                        variant={"outlined"} size={"small"}
                                        startIcon={isCheckingAll
                                            ? <CircularProgress size={12}/>
                                            : <IconRefresh size={14}/>}
                                        onClick={handleCheckAll}
                                        disabled={isCheckingAll}
                                        sx={{textTransform: "none", fontSize: 12, borderRadius: 2}}
                                    >
                                        Check All
                                    </Button>
                                </Stack>
                            </Box>
                            <CardContent sx={{pt: 1}}>
                                <Stack gap={1}>
                                    {kbTopics.map(topic => (
                                        <Stack
                                            key={topic.id}
                                            direction={"row"} alignItems={"center"} justifyContent={"space-between"}
                                            sx={{p: 1.25, borderRadius: 1.5, bgcolor: "action.hover"}}
                                        >
                                            <Stack direction={"row"} alignItems={"center"} gap={1.25}>
                                                <Box sx={{
                                                    width: 8, height: 8, borderRadius: "50%",
                                                    bgcolor: topic.server?.status === "online" ? "success.main"
                                                        : topic.server?.status === "connecting" ? "warning.main"
                                                            : "error.light",
                                                }}/>
                                                <Typography fontSize={13}>{topic.name}</Typography>
                                            </Stack>
                                            <Stack direction={"row"} alignItems={"center"} gap={1.5}>
                                                {topic.server ? (
                                                    <Typography fontSize={11} color={"text.secondary"}>
                                                        {topic.server.host}:{topic.server.port}
                                                    </Typography>
                                                ) : (
                                                    <Typography fontSize={11} color={"text.secondary"} fontStyle={"italic"}>
                                                        No server
                                                    </Typography>
                                                )}
                                                <Chip
                                                    label={`${topic.documentCount} docs`}
                                                    variant={"outlined"} size={"small"}
                                                    sx={{fontSize: 9, height: 18}}
                                                />
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>

                        <Stack direction={"row"} justifyContent={"flex-end"}>
                            <Button
                                variant={"contained"}
                                startIcon={<IconDeviceFloppy size={16}/>}
                                onClick={handleSave}
                                sx={{
                                    bgcolor: "#000", color: "#fff", borderRadius: 2, textTransform: "none",
                                    boxShadow: "none",
                                    "&:hover": {bgcolor: "#000", boxShadow: "0 4px 12px rgba(0,0,0,0.25)"},
                                }}
                            >
                                Save Settings
                            </Button>
                        </Stack>

                    </Stack>
                </Box>
            </Box>
        </KnowledgeManagerLayout>
    );
};